import { CountrySelectorBlock } from "@components/Block/blocks/CountrySelectorBlock";
import { FormAssemblyBlock } from "@components/Block/blocks/FormAssemblyBlock";
import { EloquaForm } from "@components/EloquaForm/EloquaForm";
import { IframeComponent } from "@components/Iframe/Iframe";
import { JWMCallout } from "@components/JWMCallout/JWMCallout";
import { JWMHero } from "@components/JWMHero/JWMHero";
import { JWMLink } from "@components/JWMLink";
import { RecipeGrid } from "@components/RecipeGrid/RecipeGrid";
import { Locator } from "@jmc/core/src/components/Locator/Locator";
import { Separator } from "@jmc/solid-design-system/src/components/atoms/Separator/Separator";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSBlock } from "@jwm/types/CMSBlock";
import loadable from "@loadable/component";
import { default as cmsMapper } from "@utils/cms-mapper";
import { default as getBlockType } from "@utils/getBlockType";
import React from "react";

import { INDEX_LOCATION } from "../../search/algolia-indexes";
import {
    AccordionBlock,
    ButtonBlock,
    ButtonCollectionBlock,
    CardBlock,
    CarouselBlock,
    FaqBlock,
    FragmentBlock,
    ImageBlock,
    MaterialsCardBlock,
    RowBlock,
    SectionBlock,
    SocialMediaSharingOptionsBlock,
    TabsBlock,
    TextBlock,
} from "./blocks";

const AudioCardBlock = loadable(() => import("./blocks/component/AudioCardBlock.tsx"));
const RelatedListBlock = loadable(() => import("./blocks/component/RelatedListBlock.tsx"));
const VideoCardBlock = loadable(() => import("./blocks/component/VideoCardBlock.tsx"));
const PlayerBlock = loadable(() => import("./blocks/PlayerBlock.tsx"));

import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery.tsx";
import { CMSColumnHorizontalAlign } from "@jwm/types/CMSRowProps";
import { CMSSectionProps } from "@jwm/types/CMSSectionProps";
import { mapJWMCalloutProps } from "@utils/cms-mapper/mapJWMCalloutProps";
import { mapJWMHeroProps } from "@utils/cms-mapper/mapJWMHeroProps";

import style from "./style.module.scss";
import { trackingHelpersProps } from "./types";
interface PropTypes {
    content: CMSBlock;
    fullWidth?: boolean;
    index?: number;
    locale?: string;
    placement?: string;
    refColor?: string;
    textColor?: string;
    visitedLinkColor?: string;
    sectionBackground?: string;
    sectionIndex?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    sectionContent?: CMSSectionProps;
    columnHorizontalAlignment?: CMSColumnHorizontalAlign;
    noBottomSpacing?: boolean;
    noTopPaddingFirstSection?: boolean;
}

/**
 * A Block container is responsible for instantiating the correct React component based on the Block type received from the CMS.
 */
export const Block = ({
    content,
    fullWidth = false,
    index = 0,
    locale,
    placement = "none",
    refColor,
    textColor,
    visitedLinkColor,
    sectionBackground = null,
    sectionIndex = 0,
    siteSpecialty,
    sectionContent = null,
    columnHorizontalAlignment = "Center",
    noBottomSpacing = false,
    noTopPaddingFirstSection = false,
}: PropTypes): JSX.Element => {
    const trackingHelpers: trackingHelpersProps = {
        numRows: 0,
        numPanes: 0,
        numTabs: 0,
        position: 0,
    };

    const blockType = getBlockType(content);
    const { jnjFullBranded } = useJnjBranding();
    const isMobile = useMediaQuery(BreakPoint.md);

    switch (blockType) {
        case "button": {
            trackingHelpers.position++;
            return (
                <ButtonBlock
                    {...(jnjFullBranded && isMobile && { fullWidth: true })}
                    content={content.button}
                    placement={placement}
                />
            );
        }
        case "button_collection": {
            trackingHelpers.position++;
            return <ButtonCollectionBlock content={content?.button_collection} />;
        }
        case "social_media_sharing_options": {
            return (
                <SocialMediaSharingOptionsBlock content={content?.social_media_sharing_options} placement={placement} />
            );
        }
        case "recipe_grid": {
            const { recipe_grid } = content;
            const recipeGridProps = cmsMapper.mapRecipeGridProps(recipe_grid);
            return (
                <RecipeGrid
                    specialty={recipeGridProps?.specialty}
                    enabledFilters={recipeGridProps?.enabledFilters}
                    placement={placement}
                />
            );
        }
        case "component": {
            trackingHelpers.position++;
            const component = content?.component?.component[0];
            const componentType = component?.internal?.type?.replace("Contentstack_component_", "");

            switch (componentType) {
                case "carousel": {
                    return (
                        <CarouselBlock
                            component={component}
                            placement={placement}
                            sectionIndex={sectionIndex}
                            siteSpecialty={siteSpecialty}
                        />
                    );
                }
                case "card": {
                    return (
                        <CardBlock
                            component={component}
                            placement={placement}
                            trackingHelpers={trackingHelpers}
                            sectionIndex={sectionIndex}
                            sectionLinkColor={refColor}
                            siteSpecialty={siteSpecialty}
                        />
                    );
                }
                case "hero": {
                    const heroProps = jnjFullBranded ? mapJWMCalloutProps(component) : mapJWMHeroProps(component);
                    return jnjFullBranded ? (
                        <JWMCallout
                            {...heroProps}
                            inFullWidthSection={fullWidth}
                            loading={sectionIndex === 0 ? "eager" : "lazy"}
                        ></JWMCallout>
                    ) : (
                        <JWMHero
                            {...heroProps}
                            placement={placement}
                            loading={sectionIndex === 0 ? "eager" : "lazy"}
                            siteSpecialty={siteSpecialty}
                            inFullWidthSection={fullWidth}
                        />
                    );
                }
                case "materials_card": {
                    return (
                        <MaterialsCardBlock
                            component={component}
                            placement={placement}
                            trackingHelpers={trackingHelpers}
                            sectionIndex={sectionIndex}
                            siteSpecialty={siteSpecialty}
                        />
                    );
                }
                case "audio_card": {
                    return (
                        <AudioCardBlock
                            component={component}
                            placement={placement}
                            trackingHelpers={trackingHelpers}
                            loading={sectionIndex === 0 ? "eager" : "lazy"}
                            siteSpecialty={siteSpecialty}
                        />
                    );
                }
                case "video_card": {
                    return (
                        <VideoCardBlock
                            component={component}
                            placement={placement}
                            trackingHelpers={trackingHelpers}
                            siteSpecialty={siteSpecialty}
                        />
                    );
                }
                case "related_list": {
                    return (
                        <RelatedListBlock
                            component={component}
                            placement={placement}
                            siteSpecialty={siteSpecialty}
                            sectionContent={sectionContent}
                        />
                    );
                }
                case "map": {
                    const locatorProps = cmsMapper.mapLocatorProps(component);
                    return (
                        <Locator
                            {...locatorProps}
                            name={component?.title.replace(/ /g, "")}
                            component={JWMLink}
                            placement={placement}
                            indexLocation={INDEX_LOCATION}
                        />
                    );
                }
                case "eloqua_form": {
                    return (
                        <EloquaForm
                            id={`eloquaform-${index}`}
                            form={component.html}
                            className={style.vertical_margins}
                            placement={placement}
                        />
                    );
                }
                case "formassembly": {
                    const { confirmed_compliance, source, title } = component;
                    return (
                        confirmed_compliance && (
                            <FormAssemblyBlock
                                formAssemblyRestUrl={source}
                                compliance={confirmed_compliance}
                                title={title}
                            />
                        )
                    );
                }
                case "faq": {
                    const { accordion, accordion_content, display_options, display_title, text_content, title } =
                        component;
                    return (
                        <FaqBlock
                            content={accordion ? accordion_content : text_content}
                            placement={placement}
                            sectionIndex={sectionIndex}
                            siteSpecialty={siteSpecialty}
                            title={display_options?.show_title ? display_title || title : null}
                            trackingHelpers={trackingHelpers}
                            sectionContent={sectionContent}
                            jnjFullBranded={jnjFullBranded}
                        />
                    );
                }
                case "country_selector": {
                    return <CountrySelectorBlock placement={placement} countryList={component?.countries} />;
                }
                default:
                    return <p>{`${componentType} component is not yet defined.`}</p>;
            }
        }
        case "row": {
            trackingHelpers.numRows++;
            return (
                <RowBlock
                    content={content.row}
                    placement={placement}
                    textColor={textColor}
                    refColor={refColor}
                    visitedLinkColor={visitedLinkColor}
                    locale={locale}
                    trackingHelpers={trackingHelpers}
                    sectionBackground={sectionBackground}
                    sectionIndex={sectionIndex}
                    siteSpecialty={siteSpecialty}
                />
            );
        }
        case "section": {
            return (
                <SectionBlock
                    content={content.section}
                    locale={locale}
                    placement={placement}
                    trackingHelpers={trackingHelpers}
                    index={index}
                    siteSpecialty={siteSpecialty}
                    noBottomSpacing={noBottomSpacing}
                    noTopSpacing={noTopPaddingFirstSection && index === 0}
                />
            );
        }
        case "player": {
            return <PlayerBlock content={content.player} placement={placement} trackingHelpers={trackingHelpers} />;
        }
        case "separator": {
            const { separator } = content;
            const separatorProps = cmsMapper.mapSeparatorProps(separator);
            return <Separator color={separatorProps?.color} />;
        }
        case "text": {
            return (
                <TextBlock
                    content={content.text}
                    placement={placement}
                    textColor={textColor}
                    refColor={refColor}
                    visitedLinkColor={visitedLinkColor}
                    trackingHelpers={trackingHelpers}
                    siteSpecialty={siteSpecialty}
                    sectionIndex={sectionIndex}
                />
            );
        }
        case "image": {
            return (
                <ImageBlock
                    content={content.image}
                    placement={placement}
                    trackingHelpers={trackingHelpers}
                    sectionIndex={sectionIndex}
                    columnHorizontalAlignment={columnHorizontalAlignment}
                />
            );
        }
        case "accordion": {
            return (
                <AccordionBlock
                    content={content.accordion}
                    placement={placement}
                    locale={locale}
                    trackingHelpers={trackingHelpers}
                    sectionBackground={sectionBackground}
                    sectionIndex={sectionIndex}
                    siteSpecialty={siteSpecialty}
                    sectionContent={sectionContent}
                />
            );
        }
        case "tabs": {
            return (
                <TabsBlock
                    content={content.tabs}
                    placement={placement}
                    locale={locale}
                    trackingHelpers={trackingHelpers}
                    sectionBackground={sectionBackground}
                    sectionIndex={sectionIndex}
                    siteSpecialty={siteSpecialty}
                />
            );
        }
        case "fragment": {
            return (
                <FragmentBlock
                    fragment={content.fragment}
                    placement={placement}
                    textColor={textColor}
                    refColor={refColor}
                    visitedLinkColor={visitedLinkColor}
                    locale={locale}
                    sectionBackground={sectionBackground}
                    sectionIndex={sectionIndex}
                    siteSpecialty={siteSpecialty}
                    sectionContent={sectionContent}
                />
            );
        }

        case "iframe": {
            const { iframe } = content;
            return iframe?.confirmed && <IframeComponent {...iframe} fullWidth={fullWidth} />;
        }
        default:
            trackingHelpers.position++;
            return <p>{`${blockType} is not defined.`}</p>;
    }
};

export default Block;
